import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  supportedLanguages,
  defaultLanguage,
  type SupportedLanguage,
} from '@/i18n'
import { localizedRoutes, type RouteKey } from '@/i18n/routes'

/**
 * Data-attribute used to tag every meta/link element that this hook creates
 * or owns for the lifetime of a page. When the route changes, every tagged
 * element is removed before the new page writes its own tags — this prevents
 * stale SEO data accumulating in <head> as the SPA navigates.
 */
const OWN_ATTR = 'data-page-seo'

interface HreflangAlternate {
  lang: SupportedLanguage | 'x-default'
  href: string
}

interface PageSEOOptions {
  title: string
  description: string
  ogTitle?: string
  ogDescription?: string
  ogImage?: string
  ogType?: string
  /**
   * Optional canonical URL override. When omitted the hook computes the
   * canonical from the current `location.pathname`, prefixed with the site
   * origin — which drops query strings and fragments on purpose.
   */
  canonical?: string
  /**
   * Optional overrides for the language-prefixed path used to compute
   * `<link rel="alternate" hreflang>` tags. Use this on **detail pages**
   * (machine, news, event) where the IT and EN URLs contain a shared slug
   * under differently-localised parent segments. Keys are language codes.
   *
   * Example: `{ it: '/it/macchine/fb-75', en: '/en/machines/fb-75' }`.
   *
   * When omitted, the hook infers alternates from the current route key
   * in `localizedRoutes` (works for every static page).
   */
  alternatePaths?: Partial<Record<SupportedLanguage, string>>
}

/* ==========================================================================
   Primitive helpers
   ========================================================================== */

/**
 * Resolves a URL for Open Graph / canonical tags. If the provided value is
 * already a fully qualified URL, returns it as-is; otherwise prefixes the
 * current origin (safe for SSR by falling back to the raw path).
 */
function absoluteUrl(value?: string): string | undefined {
  if (!value) return undefined
  if (/^https?:\/\//i.test(value)) return value
  if (typeof window === 'undefined') return value
  try {
    return new URL(value, window.location.origin).toString()
  } catch {
    return value
  }
}

/**
 * Creates a fresh meta element tagged with the page-seo ownership marker.
 * The hook does NOT try to reuse pre-existing tags — every render owns its
 * tags end-to-end, which makes cleanup a single-selector delete.
 */
function createMeta(attr: 'name' | 'property', key: string, content: string): HTMLMetaElement {
  const el = document.createElement('meta')
  el.setAttribute(attr, key)
  el.setAttribute('content', content)
  el.setAttribute(OWN_ATTR, '1')
  document.head.appendChild(el)
  return el
}

/**
 * Creates a fresh link element tagged with the page-seo ownership marker.
 */
function createLink(rel: string, href: string, extra?: Record<string, string>): HTMLLinkElement {
  const el = document.createElement('link')
  el.setAttribute('rel', rel)
  el.setAttribute('href', href)
  if (extra) {
    for (const [k, v] of Object.entries(extra)) {
      el.setAttribute(k, v)
    }
  }
  el.setAttribute(OWN_ATTR, '1')
  document.head.appendChild(el)
  return el
}

/**
 * Remove every meta/link element previously added by this hook. This is run
 * at the top of every effect to guarantee no two pages ever coexist in head.
 */
function clearOwnedTags(): void {
  const tagged = document.head.querySelectorAll(`[${OWN_ATTR}]`)
  tagged.forEach((node) => node.remove())
}

/* ==========================================================================
   Route → language alternates
   ========================================================================== */

/**
 * Infer the current route key by scanning the path's first segment after the
 * language prefix against `localizedRoutes`. Works for static pages (about,
 * services, contacts, …) but not for detail pages, which must pass
 * `alternatePaths` explicitly.
 */
function inferRouteKeyFromPath(
  pathname: string,
  currentLang: SupportedLanguage
): RouteKey | null {
  const parts = pathname.split('/').filter(Boolean)
  if (parts.length < 1) return null
  // After stripping the language prefix, the first segment is the route
  // segment. `/it/` (home) yields no segment.
  const segment = parts.length > 1 ? parts[1] : ''
  for (const [key, map] of Object.entries(localizedRoutes) as Array<
    [RouteKey, Record<SupportedLanguage, string>]
  >) {
    if (map[currentLang] === segment) return key
  }
  // Home case — when there's only the language prefix, map to `home`
  if (parts.length === 1 && parts[0] === currentLang) return 'home'
  return null
}

/**
 * Build the set of hreflang alternates for a page. Priority:
 *   1. `alternatePaths` override — used by detail pages
 *   2. `localizedRoutes` lookup via `inferRouteKeyFromPath`
 * Falls back to an empty list when neither yields a match (very unusual).
 */
function buildAlternates(
  pathname: string,
  currentLang: SupportedLanguage,
  override?: Partial<Record<SupportedLanguage, string>>
): HreflangAlternate[] {
  const entries: HreflangAlternate[] = []
  const ordered: SupportedLanguage[] = [...supportedLanguages]

  if (override) {
    for (const lang of ordered) {
      const href = override[lang]
      if (href) entries.push({ lang, href })
    }
  } else {
    const routeKey = inferRouteKeyFromPath(pathname, currentLang)
    if (routeKey) {
      for (const lang of ordered) {
        const segment = localizedRoutes[routeKey][lang]
        const href = segment ? `/${lang}/${segment}` : `/${lang}`
        entries.push({ lang, href })
      }
    }
  }

  // Always emit x-default when we have a default-language alternate.
  const defaultEntry = entries.find((e) => e.lang === defaultLanguage)
  if (defaultEntry) {
    entries.push({ lang: 'x-default', href: defaultEntry.href })
  }

  return entries
}

/* ==========================================================================
   Public hook
   ========================================================================== */

/**
 * usePageSEO — the single source of truth for document head management.
 *
 * Sets:
 *   - `document.title`
 *   - `<meta name="description">`
 *   - Open Graph: `og:title`, `og:description`, `og:image`, `og:type`,
 *     `og:url`, `og:site_name`, `og:locale`
 *   - Twitter: `summary_large_image` card, title, description, image
 *   - `<link rel="canonical">` (path-based — strips query + fragment)
 *   - `<link rel="alternate" hreflang="…">` for each supported language
 *     plus `hreflang="x-default"` pointing at the default-language URL
 *
 * Every tag written is marked with `data-page-seo` so the next invocation
 * can drop stale tags wholesale before writing its own — no risk of stale
 * OG tags bleeding between routes in the SPA.
 */
export function usePageSEO({
  title,
  description,
  ogTitle,
  ogDescription,
  ogImage,
  ogType = 'website',
  canonical,
  alternatePaths,
}: PageSEOOptions): void {
  const { i18n } = useTranslation()
  const location = useLocation()
  const currentLang = (i18n.language || defaultLanguage).split('-')[0] as SupportedLanguage

  // Serialise override so we can safely depend on it from the effect without
  // triggering unnecessary re-runs.
  const overrideKey = alternatePaths ? JSON.stringify(alternatePaths) : ''

  useEffect(() => {
    const prevTitle = document.title
    document.title = title

    // Always start from a clean slate — every tag we wrote previously is
    // removed, then we re-create everything from scratch.
    clearOwnedTags()

    createMeta('name', 'description', description)

    // --- Open Graph ---------------------------------------------------
    createMeta('property', 'og:title', ogTitle ?? title)
    createMeta('property', 'og:description', ogDescription ?? description)
    createMeta('property', 'og:type', ogType)
    createMeta('property', 'og:site_name', 'Fenix Srl')
    createMeta('property', 'og:locale', currentLang === 'it' ? 'it_IT' : 'en_GB')

    const resolvedImage = absoluteUrl(ogImage)
    if (resolvedImage) {
      createMeta('property', 'og:image', resolvedImage)
      createMeta('name', 'twitter:image', resolvedImage)
    }

    // --- Twitter ------------------------------------------------------
    createMeta('name', 'twitter:card', 'summary_large_image')
    createMeta('name', 'twitter:title', ogTitle ?? title)
    createMeta('name', 'twitter:description', ogDescription ?? description)

    // --- Canonical + og:url ------------------------------------------
    if (typeof window !== 'undefined') {
      // Canonical: path-only, NEVER include query or fragment.
      const canonicalPath = canonical ?? location.pathname
      const canonicalUrl = absoluteUrl(canonicalPath)
      if (canonicalUrl) {
        createLink('canonical', canonicalUrl)
        createMeta('property', 'og:url', canonicalUrl)
      }

      // --- hreflang alternates -----------------------------------------
      const alternates = buildAlternates(
        location.pathname,
        currentLang,
        alternatePaths
      )
      for (const alt of alternates) {
        const href = absoluteUrl(alt.href)
        if (!href) continue
        createLink('alternate', href, { hreflang: alt.lang })
      }
    }

    return () => {
      document.title = prevTitle
      clearOwnedTags()
    }
  }, [
    title,
    description,
    ogTitle,
    ogDescription,
    ogImage,
    ogType,
    canonical,
    currentLang,
    location.pathname,
    overrideKey,
    alternatePaths,
  ])
}
